/**
 * Hydrometric Gauges layer (Flood Risk → "Hydrometric Gauges") — the first live
 * render of the E.F.1 hydrometric_observations ingest (WSC GeoMet OGC, ~18 northern-
 * Ontario river gauges). The ambient flood detect signal: "how fast is the river
 * climbing" (operator-cop-doctrine §2.7).
 *
 * Per §3B.8 gauges live in the AMBIENT register: a round dot SIZED + COLOURED by the
 * rate-of-rise band from the context-locked flood palette (styles/floodGauges),
 * NEVER pulsing (motion is reserved for the scored Anomaly-Queue dots). A gauge-sparse
 * station (recon §B: Fort Severn / Peawanuck / Wapekeka) renders hollow to flag the
 * honest low-confidence coverage.
 *
 * Render gate (build.md § L.2 + §9.1, the power-outages precedent): HIDDEN at country
 * zoom, visible regional+. Each dot carries a distanceDisplayCondition with
 * far = REGIONAL_VISIBLE_FAR_M so the national Default view never clutters.
 *
 * Imperative installer (MapView owns the viewer), mirroring PowerOutagesLayer:
 * requestRefresh on camera moves, setVisible on the toggle, pick from the central
 * click handler → HydrometricGaugeFeature → the HydrometricGaugeBody side panel.
 */
import * as Cesium from 'cesium';
import {
  fetchHydrometricGauges,
  type BboxQuery,
  type HydrometricGaugeFeature,
  type HydrometricGaugeFeatureCollection,
} from '../api';
import { gaugeBandStyle, gaugeDotSize, GAUGE_DOT_ALPHA } from '../styles/floodGauges';
import { REGIONAL_VISIBLE_FAR_M } from '../coneVisibility';

const REFRESH_DEBOUNCE_MS = 300;
const GAUGE_PREFIX = 'gauge:';

export interface HydrometricGaugesLayerOptions {
  onCount?: (n: number) => void;
  getVisible?: () => boolean;
  fetcher?: (bbox: BboxQuery, signal: AbortSignal) => Promise<HydrometricGaugeFeatureCollection>;
}

export interface HydrometricGaugesLayerHandle {
  requestRefresh(bbox: BboxQuery): void;
  refreshNow(bbox: BboxQuery): Promise<void>;
  setVisible(visible: boolean): void;
  pick(entity: unknown): HydrometricGaugeFeature | null;
  destroy(): void;
}

export function installHydrometricGaugesLayer(
  viewer: Cesium.Viewer,
  opts: HydrometricGaugesLayerOptions = {},
): HydrometricGaugesLayerHandle {
  const fetcher = opts.fetcher ?? fetchHydrometricGauges;
  const ds = new Cesium.CustomDataSource('hydrometric-gauges');
  void viewer.dataSources.add(ds);

  // Hide at country zoom, show regional+ (the L.2 render gate).
  const gate = new Cesium.DistanceDisplayCondition(0, REGIONAL_VISIBLE_FAR_M);

  const entityToFeature = new Map<Cesium.Entity, HydrometricGaugeFeature>();
  let inflight: AbortController | null = null;
  let debounceTimer: ReturnType<typeof setTimeout> | null = null;
  let destroyed = false;
  let loadGen = 0;

  const render = (fc: HydrometricGaugeFeatureCollection): void => {
    ds.entities.removeAll();
    entityToFeature.clear();
    for (const feature of fc.features) {
      const rate = feature.properties.rate_of_rise_cm_per_hr;
      const style = gaugeBandStyle(rate);
      const color = Cesium.Color.fromCssColorString(style.color);
      const [lon, lat] = feature.geometry.coordinates;
      // Gauge-sparse stations render HOLLOW (low fill, thicker ring) — honest
      // low-confidence coverage (recon §B), never the same as a real reading.
      const sparse = feature.properties.gauge_sparse;
      const dot = ds.entities.add({
        id: `${GAUGE_PREFIX}${feature.id}`,
        position: Cesium.Cartesian3.fromDegrees(lon, lat),
        point: {
          pixelSize: gaugeDotSize(rate),
          color: color.withAlpha(sparse ? 0.18 : GAUGE_DOT_ALPHA),
          outlineColor: color.withAlpha(sparse ? 0.9 : 1),
          outlineWidth: sparse ? 2 : 1,
          disableDepthTestDistance: Number.POSITIVE_INFINITY,
          distanceDisplayCondition: gate,
        },
      });
      entityToFeature.set(dot, feature);
    }
  };

  const refreshNow = async (bbox: BboxQuery): Promise<void> => {
    if (destroyed) return;
    if (inflight) inflight.abort();
    const ctl = new AbortController();
    inflight = ctl;
    const gen = ++loadGen;
    try {
      const fc = await fetcher(bbox, ctl.signal);
      if (destroyed || ctl.signal.aborted || gen !== loadGen) return;
      render(fc);
      opts.onCount?.(fc.features.length);
      ds.show = opts.getVisible ? opts.getVisible() : true;
    } catch (err) {
      if ((err as { name?: string }).name === 'AbortError') return;
      console.error('fetchHydrometricGauges failed', err);
    }
  };

  return {
    requestRefresh(bbox: BboxQuery): void {
      if (debounceTimer) clearTimeout(debounceTimer);
      debounceTimer = setTimeout(() => void refreshNow(bbox), REFRESH_DEBOUNCE_MS);
    },
    refreshNow,
    setVisible(visible: boolean): void {
      ds.show = visible;
    },
    pick(entity: unknown): HydrometricGaugeFeature | null {
      if (!entity || typeof entity !== 'object') return null;
      return entityToFeature.get(entity as Cesium.Entity) ?? null;
    },
    destroy(): void {
      destroyed = true;
      if (debounceTimer) clearTimeout(debounceTimer);
      if (inflight) inflight.abort();
      entityToFeature.clear();
      if (!viewer.isDestroyed()) viewer.dataSources.remove(ds, true);
    },
  };
}
